import { delay } from "@/utilities/delay";
import { triggerHaptic } from "@/utilities/haptics";
import { buildAnalyticsContext } from "@/features/betslip/services/submitBet/analytics";
import {
  fetchFollowUps,
  submitBetMessage,
} from "@/features/betslip/services/submitBet/messageBus";
import { writeLastSubmitted } from "@/features/betslip/services/submitBet/persistence";
import { preparePayload } from "@/features/betslip/services/submitBet/payload";
import { checkPreflight } from "@/features/betslip/services/submitBet/preflight";
import {
  processResponse,
  resolveInterceptedOutcomeIds,
} from "@/features/betslip/services/submitBet/response";
import { reportTransportError } from "@/features/betslip/services/submitBet/transport";
import { isHeldOverlay } from "@/features/betslip/utils/typeGuards";
import {
  HELD_CODE,
  type BetResponse,
  type HeldBetResponse,
  type HeldKind,
  type RejectedBetResponse,
  type SubmitBetParams,
} from "@/features/betslip/services/submitBet/types";

export type {
  BetslipStoreHandle,
  SubmitBetParams,
} from "@/features/betslip/services/submitBet/types";

// TODO: replace the post-rejection delay with a real subscription to the
// Firestore balance — this is a band-aid so the UI doesn't re-render with
// the stale balance before the credit lands.
const BALANCE_REFRESH_DELAY_MS = 500;
const HELD_TIMEOUT_BUFFER_MS = 5_000;
const HELD_MIN_WAIT_MS = 10_000;

export const submitBet = async (params: SubmitBetParams): Promise<void> => {
  const { store: storeHandle } = params;
  const state = storeHandle.getState();

  // 1. Pre-flight: phase guard, duplicate check.
  const preflight = checkPreflight(state, params);
  if (preflight.kind === "skip") return;
  if (preflight.kind === "duplicate") {
    state.setOverlay({ type: "duplicate" });
    return;
  }

  // 2. Transition to submitting; clear stale results.
  state.clearResults();
  state.confirmBet();

  // 3. Build payload + analytics context.
  const {
    payload,
    multiLegCorrelationIds,
    multiCorrelationId,
    betCorrelationToSelection,
  } = preparePayload(state, params, preflight.hasMultiStake);
  const analytics = buildAnalyticsContext({
    store: state,
    params,
    stakedSingles: preflight.stakedSingles,
    multiLegCorrelationIds,
    hasMultiStake: preflight.hasMultiStake,
  });

  // 4. Send. The backend streams a `bets-submitted` wrapper first, then
  // one `bet-accepted`, `-rejected`, or `-intercepted` per bet, so listen
  // for `bets.length + 1` to catch every per-bet outcome.
  const processContext = {
    storeHandle,
    isMulti: preflight.hasMultiStake,
    multiCorrelationId,
    correlationToOutcome: analytics.correlationToOutcome,
    betCorrelationToSelection,
    analytics,
  };

  const [responses, error, messageId] = await submitBetMessage(
    payload,
    payload.bets.length + 1,
    (response) => markHeldEarly(response, storeHandle, processContext),
  );

  if (error || !responses) {
    // markHeldEarly may have raised a held overlay mid-stream. Nothing else
    // clears it on this path, and a held overlay keeps the slip in `isPlacing`
    // forever — the user couldn't even remove a selection.
    clearHeldOverlay(storeHandle);
    state.responsesProcessed();
    reportTransportError(state, error);
    return;
  }

  // 5. Process the first batch immediately so accepted/rejected results
  //    are visible while any held bets resolve.
  await awaitBalanceRefresh(responses);
  for (const resp of responses) {
    if (isHeld(resp)) continue;
    processResponse(resp, processContext);
  }

  // 6. Held bets: a live delay comes back before the real result, and the
  //    server can hold a bet for trader review. Both send a timeoutAt, so
  //    wait again for the follow-ups and process only those. Delays resolve
  //    first — a delayed bet can still come back intercepted.
  // Every response seen so far, held ones included — each wait re-reads the
  // whole message thread, so the ask has to grow with it.
  let pool = responses;
  let expectedResponses = payload.bets.length + 1;
  const seen = new Set(responses.map(responseKey));

  for (const kind of HELD_KINDS) {
    const held = pool.filter(
      (r): r is HeldBetResponse => r.code === HELD_CODE[kind],
    );
    if (held.length === 0) continue;

    expectedResponses += held.length;
    const followUps = await handleHeld({
      kind,
      held,
      seen,
      expectedResponses,
      messageId,
      storeHandle,
      processContext,
    });

    pool = [...pool, ...followUps];
    for (const resp of followUps) seen.add(responseKey(resp));
  }

  // 7. Persist the last-submitted snapshot for duplicate detection only when
  //    nothing was rejected. A rejected submit placed no bet, so the next
  //    attempt must not be flagged as a duplicate. (Without this, a fast-moving
  //    market that keeps rejecting on odds changes — e.g. greyhound multis —
  //    loops between the accept-odds and duplicate prompts forever.)
  const anyRejected = pool.some((r) => r.code === "bet-rejected");
  if (!anyRejected) {
    writeLastSubmitted(params.userId, storeHandle.getState());
  }

  // 8. Return to idle.
  state.responsesProcessed();
};

const isRejectedWithAlternativeStake = (
  r: BetResponse,
): r is RejectedBetResponse =>
  r.code === "bet-rejected" && r.data.alternativeStake !== undefined;

// A rejection with an alternative stake means the backend refunded, so hold
// off rendering until the Firestore balance catches up. Must run BEFORE the
// responses are processed — after is too late, the stale balance is already
// on screen.
const awaitBalanceRefresh = async (responses: BetResponse[]): Promise<void> => {
  if (responses.some(isRejectedWithAlternativeStake)) {
    await delay(BALANCE_REFRESH_DELAY_MS);
  }
};

const HELD_KINDS = ["delayed", "intercepted"] satisfies HeldKind[];

const HELD_CODES: string[] = Object.values(HELD_CODE);

const isHeld = (r: BetResponse): r is HeldBetResponse =>
  HELD_CODES.includes(r.code);

const heldKindOf = (r: HeldBetResponse): HeldKind =>
  HELD_KINDS.find((kind) => HELD_CODE[kind] === r.code) ?? "intercepted";

// Drop a held overlay if one is up. A held overlay reads as `isPlacing`, so
// leaving one behind locks the slip.
const clearHeldOverlay = (storeHandle: SubmitBetParams["store"]): void => {
  const state = storeHandle.getState();
  if (isHeldOverlay(state.overlay)) {
    state.setOverlay({ type: "none" });
  }
};

const responseKey = (r: BetResponse): string =>
  `${r.code}:${r.data.correlationId ?? ""}`;

const heldOverlay = (
  kind: HeldKind,
  held: HeldBetResponse[],
  processContext: Parameters<typeof processResponse>[1],
) => {
  // The last of the held bets to time out, ignoring any the server left off
  // or sent unparseable.
  let latest: number | undefined;
  for (const { data } of held) {
    if (!data.timeoutAt) continue;
    const at = new Date(data.timeoutAt).getTime();
    if (Number.isNaN(at)) continue;
    if (latest === undefined || at > latest) latest = at;
  }

  return {
    type: kind,
    timeoutAt: new Date(latest ?? Date.now()),
    outcomeIds: resolveInterceptedOutcomeIds(held, processContext),
  } as const;
};

/**
 * Flip the overlay as soon as the first held notice streams in, rather than
 * waiting for the whole batch. First one wins: a delay that later turns into
 * a trader review keeps showing "confirming" until the delay resolves.
 */
const markHeldEarly = (
  response: BetResponse,
  storeHandle: SubmitBetParams["store"],
  processContext: Parameters<typeof processResponse>[1],
): void => {
  if (!isHeld(response)) return;
  const state = storeHandle.getState();
  if (isHeldOverlay(state.overlay)) return;
  state.setOverlay(
    heldOverlay(heldKindOf(response), [response], processContext),
  );
};

type HandleHeldInput = {
  kind: HeldKind;
  held: HeldBetResponse[];
  /** Keys of every response already processed — replayed docs to skip. */
  seen: Set<string>;
  /** Cumulative doc count to ask for: originals + every held follow-up. */
  expectedResponses: number;
  messageId: string;
  storeHandle: SubmitBetParams["store"];
  processContext: Parameters<typeof processResponse>[1];
};

const handleHeld = async ({
  kind,
  held,
  seen,
  expectedResponses,
  messageId,
  storeHandle,
  processContext,
}: HandleHeldInput): Promise<BetResponse[]> => {
  const state = storeHandle.getState();
  // A trader review is bad news worth buzzing about. A live delay is just how
  // live betting works, so it stays quiet.
  if (kind === "intercepted") triggerHaptic.error();

  // Phase stays `submitting`. The overlay carries the visible held state;
  // only the held bets' own cards show it.
  const overlay = heldOverlay(kind, held, processContext);
  const current = storeHandle.getState().overlay;
  if (!isHeldOverlay(current) || current.type === kind) {
    state.setOverlay(overlay);
  }

  const remainingMs = Math.max(
    overlay.timeoutAt.getTime() - Date.now() + HELD_TIMEOUT_BUFFER_MS,
    HELD_MIN_WAIT_MS,
  );

  // A fresh subscription replays every response doc already delivered
  // (wrapper + one per bet), THEN the follow-ups arrive. Ask for the
  // full set — requesting only `held.length` would return the replayed
  // originals and never the follow-ups.
  const [followUpBatch, followUpError] = await fetchFollowUps(messageId, {
    numResponses: expectedResponses,
    timeOutMs: remainingMs,
  });

  let newResponses: BetResponse[] = [];
  if (followUpError) {
    reportTransportError(state, followUpError);
  } else if (followUpBatch) {
    // Keep only docs we haven't already processed. The follow-up for a held
    // bet shares its correlationId but arrives under a new code
    // (accepted/rejected), so it survives this filter.
    newResponses = followUpBatch.filter((r) => !seen.has(responseKey(r)));
    await awaitBalanceRefresh(newResponses);
    for (const resp of newResponses) {
      if (isHeld(resp)) continue; // handled by the next pass, not per-response
      processResponse(resp, processContext);
    }
  }

  // Clear this overlay once the follow-ups (or the timeout) resolve. The next
  // held kind sets its own.
  if (storeHandle.getState().overlay.type === kind) {
    state.setOverlay({ type: "none" });
  }

  return newResponses;
};
